import { useEffect, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { AlertTriangle, Bell, CheckCircle2, MessageSquare, XCircle } from "lucide-react";
import type { ComponentType } from "react";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useEvent } from "../react/hooks/useEvent";
import type { BackendNotification, Json, NotificationLevel } from "../core";

const LEVEL_ICON: Record<NotificationLevel, ComponentType<{ size?: number; className?: string }>> = {
    info: MessageSquare,
    success: CheckCircle2,
    warning: AlertTriangle,
    error: XCircle,
};

// Header bell — list, mark-read, dismiss. Mirrors the original frontend's
// UserNotification.tsx. Hydrates on first open + on each subsequent open so
// SSE-driven server changes are picked up without leaking timers when closed.
export function UserNotification() {
    const runtime = useRuntime();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState<BackendNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);

    // Hydrate on mount.
    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const records = await runtime.services.notification.list({ limit: 50, includeDismissed: false });
                if (cancelled) return;
                setItems(records);
                setUnreadCount(records.filter((r) => !r.read_at_utc).length);
            } catch {
                // Silent: bell stays empty if hydration fails.
            }
        }
        void load();
        return () => {
            cancelled = true;
        };
    }, [runtime]);

    // Live SSE updates — fan in from the EventBus topic `web.client.notification`.
    useEvent("web.client.notification", (payload: Json) => {
        if (!payload || typeof payload !== "object" || Array.isArray(payload)) return;
        const p = payload as Record<string, unknown>;
        const notificationId = typeof p.notification_id === "string" ? p.notification_id : "";
        if (!notificationId) {
            // System-level toast that's not a persisted notification — skip the bell.
            return;
        }
        const level: NotificationLevel = (typeof p.level === "string" && (p.level === "info" || p.level === "success" || p.level === "warning" || p.level === "error"))
            ? p.level
            : "info";
        const newItem: BackendNotification = {
            record_uuid: notificationId,
            event_key: typeof p.event_key === "string" ? p.event_key : "",
            subject: typeof p.subject === "string" ? p.subject : "",
            body: typeof p.message === "string" ? p.message : "",
            level,
            action_url: typeof p.action_url === "string" ? p.action_url : "",
            created_at_utc: new Date().toISOString(),
            read_at_utc: null,
            dismissed_at_utc: null,
        };
        setItems((prev) => [newItem, ...prev.filter((row) => row.record_uuid !== newItem.record_uuid)]);
        setUnreadCount((c) => c + 1);
    });

    async function handleMarkAllRead() {
        try {
            await runtime.services.notification.markAllRead();
            const now = new Date().toISOString();
            setItems((prev) => prev.map((row) => (row.read_at_utc ? row : { ...row, read_at_utc: now })));
            setUnreadCount(0);
        } catch {
            // ignore
        }
    }

    async function handleClickItem(item: BackendNotification) {
        if (!item.read_at_utc) {
            try {
                await runtime.services.notification.markRead(item.record_uuid);
                setItems((prev) =>
                    prev.map((row) =>
                        row.record_uuid === item.record_uuid
                            ? { ...row, read_at_utc: new Date().toISOString() }
                            : row,
                    ),
                );
                setUnreadCount((c) => Math.max(0, c - 1));
            } catch {
                // ignore
            }
        }
        if (item.action_url) {
            setOpen(false);
            window.location.href = item.action_url;
        }
    }

    async function handleDismiss(event: React.MouseEvent, item: BackendNotification) {
        event.stopPropagation();
        try {
            await runtime.services.notification.dismiss(item.record_uuid);
        } catch {
            // ignore — still hide locally
        }
        setItems((prev) => prev.filter((row) => row.record_uuid !== item.record_uuid));
        if (!item.read_at_utc) {
            setUnreadCount((c) => Math.max(0, c - 1));
        }
    }

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button className="icon-btn" type="button" title="Notifications" aria-label="Notifications" data-viewid="ede-notification-bell">
                    <Bell size={16} />
                    {unreadCount > 0 ? <span className="notif-dot" aria-hidden="true" /> : null}
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content className="notif-pane" align="end" sideOffset={6}>
                    <div className="notif-pane__header">
                        <span className="notif-pane__title">Notifications</span>
                        {unreadCount > 0 ? (
                            <button type="button" className="notif-pane__link" onClick={() => void handleMarkAllRead()} data-viewid="ede-notification-mark-read">
                                Mark all as read
                            </button>
                        ) : null}
                    </div>

                    {items.length === 0 ? (
                        <div className="notif-pane__empty">
                            <Bell size={28} className="notif-pane__empty-icon" />
                            <span>You&apos;re all caught up</span>
                        </div>
                    ) : (
                        <div className="notif-pane__list">
                            {items.map((item) => {
                                const Icon = LEVEL_ICON[item.level] ?? MessageSquare;
                                const unread = !item.read_at_utc;
                                return (
                                    <button
                                        key={item.record_uuid}
                                        type="button"
                                        onClick={() => void handleClickItem(item)}
                                        className={`notif-item${unread ? " notif-item--unread" : ""}`}
                                        data-viewid="ede-notification-item"
                                        data-notification-id={item.record_uuid}
                                    >
                                        <span className={`notif-item__icon notif-item__icon--${item.level}`}>
                                            <Icon size={14} />
                                        </span>
                                        <span className="notif-item__body">
                                            <span className="notif-item__subject">{item.subject || item.event_key}</span>
                                            {item.body ? <span className="notif-item__message">{item.body}</span> : null}
                                            <span className="notif-item__time">{formatRelative(item.created_at_utc)}</span>
                                        </span>
                                        <span className="notif-item__actions">
                                            {unread ? <span className="notif-item__unread-dot" aria-hidden="true" /> : null}
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                className="notif-item__dismiss"
                                                onClick={(ev) => void handleDismiss(ev, item)}
                                                onKeyDown={(ev) => {
                                                    if (ev.key === "Enter" || ev.key === " ") {
                                                        void handleDismiss(ev as unknown as React.MouseEvent, item);
                                                    }
                                                }}
                                                title="Dismiss"
                                                aria-label="Dismiss"
                                            >
                                                ×
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

// Compact relative-time label (en-US). Matches the original feel — minutes / hours /
// days, falling back to a locale date for older items.
function formatRelative(iso: string): string {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "";
    const diff = Date.now() - t;
    const m = Math.round(diff / 60_000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
}
